import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useDarkMode } from '../../../hooks/use-dark-mode.hook';
import type { OperatorHourlyRow, RouteHourlyRow } from '../../../services/reports.service';
import { CPD_COLORS } from '../dashboard/dashboard.charts';

type Row = OperatorHourlyRow | RouteHourlyRow;

/**
 * Columnas apiladas humano / buzón / sin veredicto por hora. Si hay varias series
 * (operadores o rutas) se suman por hora — el filtro de la pantalla ya acota una.
 */
export default function HourlyChart({ rows, height = 320 }: { rows: Row[]; height?: number }) {
  const isDark = useDarkMode();
  const options = useMemo<Highcharts.Options>(() => {
    const text = isDark ? '#cdcdde' : '#3f4254';
    const grid = isDark ? '#2b2b40' : '#eff2f5';
    const byHour = new Map<
      string,
      { human: number; voicemail: number; unknown: number; total: number }
    >();
    for (const r of rows) {
      const cur = byHour.get(r.hour) ?? { human: 0, voicemail: 0, unknown: 0, total: 0 };
      cur.human += r.human;
      cur.voicemail += r.voicemail;
      cur.unknown += r.unknown;
      cur.total += r.total;
      byHour.set(r.hour, cur);
    }
    const hours = Array.from(byHour.keys()).sort();
    const x = hours.map((h) => new Date(h).getTime());
    const pick = (k: 'human' | 'voicemail' | 'unknown' | 'total') =>
      hours.map((h, i) => [x[i], byHour.get(h)![k]]);
    return {
      chart: {
        backgroundColor: 'transparent',
        height,
        animation: false,
        style: { fontFamily: 'inherit' }
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: { itemStyle: { color: text }, itemHoverStyle: { color: text } },
      xAxis: {
        type: 'datetime',
        labels: { style: { color: text }, format: '{value:%d %b %H:%M}' },
        lineColor: grid,
        tickColor: grid
      },
      yAxis: [
        {
          title: { text: 'veredictos', style: { color: text } },
          labels: { style: { color: text } },
          gridLineColor: grid,
          allowDecimals: false,
          min: 0
        },
        {
          title: { text: 'llamadas', style: { color: text } },
          labels: { style: { color: text } },
          opposite: true,
          allowDecimals: false,
          min: 0
        }
      ],
      tooltip: { shared: true, xDateFormat: '%d %b %H:%M' },
      plotOptions: { series: { animation: false }, column: { stacking: 'normal', borderWidth: 0 } },
      series: [
        { type: 'column', name: 'Humano', color: CPD_COLORS.human, data: pick('human') },
        { type: 'column', name: 'Buzón', color: CPD_COLORS.voicemail, data: pick('voicemail') },
        { type: 'column', name: 'Sin veredicto', color: CPD_COLORS.unknown, data: pick('unknown') },
        {
          type: 'line',
          name: 'Total llamadas',
          color: CPD_COLORS.newCalls,
          yAxis: 1,
          data: pick('total'),
          marker: { enabled: false }
        }
      ]
    };
  }, [rows, isDark, height]);

  return <HighchartsReact highcharts={Highcharts} options={options} />;
}
